import React, { useCallback, useEffect, useRef, useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import {
  getDealerAdminInquiry,
  updateDealerAdminInquiry,
  replyToDealerAdminInquiry,
  createDealerAdminInspection,
  openDealerGuestAccount,
  splitDealerAdminInquiryCar,
  getDealerAdminMergeTargets,
  mergeDealerAdminInquiry,
  leadCars,
  INQUIRY_STATUSES,
  INQUIRY_STATUS_LABELS,
  INQUIRY_TYPE_LABELS,
  formatUsd,
} from "../../../services/dealerGarage";
import DepositRequestBox from "./DepositRequestBox";
import DealerWaitBox from "./DealerWaitBox";
import GuestAccountBox from "./GuestAccountBox";
import PanelFrame from "./PanelFrame";
import MoreMenu from "./MoreMenu";
import StaffNotesBox from "./StaffNotesBox";
import CustomerLink from "./CustomerLink";
import { VehicleThumb } from "./PurchasePanel";
import InquiryThread from "../../GarageContent/InquiryThread";
import {
  formatDate, formatPhone, vehicleLabel, customerName, customerRefOf, errorMessage, ageLabel, hoursSince,
  leadAttention, REPLY_ALERT_HOURS,
} from "./dealerAdmin";

// The account fields the API adds to a lead (adminGetInquiry), from an account summary.
const accountFields = (account) => ({
  accountUserId: account._id,
  accountIsGuest: !!account.isGuest,
  depositBalance: account.depositBalance || 0,
});

// "Imported, port Baltimore, US title service, estimate shown $18,000"; "" with no quote.
const quoteLine = (quote) => (quote && quote.quoteType
  ? [
    quote.quoteType === "imported" ? "Imported" : "Shipped",
    quote.port ? `port ${quote.port}` : "",
    quote.includeTitle ? "US title service" : "",
    quote.estimatedTotal ? `estimate shown ${formatUsd(quote.estimatedTotal)}` : "",
  ].filter(Boolean).join(", ")
  : "");

// The lead as it reads for one of its cars, for the sale form.
const withCar = (inquiry, car) => (car && !car.primary
  ? { ...inquiry, vehicleId: car.vehicleId, source: car.source, vehicle: car.vehicle, type: car.type, quote: car.quote }
  : inquiry);

// "/dealer-stock/66a0...?utm_source=x" from a full page URL, for the details card.
const pagePath = (page) => {
  try {
    const url = new URL(page);
    return `${url.pathname}${url.search}`;
  } catch (err) {
    return page;
  }
};

// Where the visitor first came from: "Google Ads · jdm imports", "" when unknown.
const firstTouch = (attribution) => {
  const a = attribution || {};
  return [a.channel || a.utmSource, a.utmCampaign || a.utmTerm].filter(Boolean).join(" · ");
};

// A lead's cars, each with what was asked about it; Split for the extra ones.
const CarRow = ({ car, busy, onSplit }) => (
  <li>
    <VehicleThumb record={car} className={styles.cardThumb} />
    <div className={styles.carInfo}>
      {car.vehicle && car.vehicle.url
        ? <a href={car.vehicle.url} target="_blank" rel="noopener noreferrer" className={classes.clickableCell}>{vehicleLabel(car)}</a>
        : <strong>{vehicleLabel(car)}</strong>}
      <div className={styles.hint}>
        {[
          INQUIRY_TYPE_LABELS[car.type] || "Inquiry",
          quoteLine(car.type === "quote" && car.quote),
          car.vehicle && car.vehicle.usdPrice ? `listed ${formatUsd(car.vehicle.usdPrice, { cents: false })}` : "",
          `${car.primary ? "first asked" : "added"} ${formatDate(car.addedAt)}`,
        ].filter(Boolean).join(" · ")}
      </div>
      {onSplit && (
        <button type="button" className={styles.btnText} style={{ paddingLeft: 0 }} disabled={busy} onClick={() => onSplit(car)}>
          Split into its own lead
        </button>
      )}
    </div>
  </li>
);

/**
 * One lead, opened over the board or the Inquiries list. The header holds who
 * and what it is, the status (saved as soon as it's picked) and the way on to
 * an inspection or a sale; under it, the one thing that needs doing now (the
 * call to make, the reply they're waiting for...). The conversation fills the
 * main column; the cars, deposit, the wait on the Japanese dealer, staff notes
 * and where the lead came from sit beside it. Loading it is the staff read
 * receipt (clears the unread count).
 *
 * Props: inquiryId, onClose(), onChanged(inquiry), onOpenInspection(id),
 * onRecordPurchase(inquiry), onOpenCustomer(ref) - the name opens their page,
 * onOpenLead(id) - another lead takes this panel's place
 */
const InquiryPanel = ({ inquiryId, onClose, onChanged, onOpenInspection, onRecordPurchase, onOpenCustomer, onOpenLead }) => {
  const [inquiry, setInquiry] = useState(null);
  // Staff notes and "what they want instead", as typed: filled on the first
  // load and saved on their own, so other saves never clear them.
  const [notes, setNotes] = useState(null);
  const [searchNotes, setSearchNotes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [replying, setReplying] = useState(false);
  // "Which car?" on a lead with several: "inspection" or "sale" while asking.
  const [picking, setPicking] = useState("");
  const [pickedCar, setPickedCar] = useState("");
  // "Move into which lead?": the customer's other open leads while asking.
  const [mergeTargets, setMergeTargets] = useState(null);
  const [mergeInto, setMergeInto] = useState("");
  const [notice, setNotice] = useState("");
  const [statusSaved, setStatusSaved] = useState(false);
  const threadRef = useRef(null);

  const load = useCallback(() => {
    getDealerAdminInquiry(inquiryId)
      .then((res) => {
        if (res?.message === "Success" && res.data) {
          setInquiry(res.data);
          setNotes((n) => (n === null ? res.data.adminNotes || "" : n));
          setSearchNotes((n) => (n === null ? res.data.searchNotes || "" : n));
        } else {
          alert(errorMessage(res, "Could not load the lead."));
        }
      })
      .catch(() => alert("Could not load the lead."));
  }, [inquiryId]);

  useEffect(() => { load(); }, [load]);

  // "Saved" beside the status for a moment after it changes.
  useEffect(() => {
    if (!statusSaved) return undefined;
    const timer = setTimeout(() => setStatusSaved(false), 2500);
    return () => clearTimeout(timer);
  }, [statusSaved]);

  // Saves part of the lead and takes the result back; the account fields the
  // update doesn't send stay as they were. Resolves to whether it saved.
  const update = async (body, fallback = "Could not update the lead.") => {
    setBusy(true);
    try {
      const res = await updateDealerAdminInquiry(inquiryId, body);
      if (res?.message === "Inquiry updated" && res.data) {
        setInquiry((i) => ({ ...i, ...res.data }));
        if (onChanged) onChanged(res.data);
        return true;
      }
      alert(errorMessage(res, fallback));
      return false;
    } catch (err) {
      alert(fallback);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (body, files) => {
    setReplying(true);
    try {
      const res = await replyToDealerAdminInquiry(inquiryId, body, files);
      if (res?.message === "Reply sent" && res.data) {
        // The first reply moves a new lead to Contacted.
        setInquiry((i) => ({ ...i, ...res.data }));
        if (onChanged) onChanged(res.data);
        return true;
      }
      alert(res?.message || "Could not send the reply.");
      return false;
    } catch (err) {
      alert("Could not send the reply.");
      return false;
    } finally {
      setReplying(false);
    }
  };

  const saveStatus = async (status) => {
    if (await update({ status })) setStatusSaved(true);
  };

  const saveNotes = () => update({ adminNotes: notes }, "Could not save the notes.");
  const saveSearchNotes = () => update({ searchNotes }, "Could not save that.");

  const saveDealerWait = async (body) => {
    const res = await updateDealerAdminInquiry(inquiryId, body).catch(() => null);
    if (res?.message === "Inquiry updated") {
      setInquiry((i) => ({ ...i, dealerAsk: res.data.dealerAsk }));
      if (onChanged) onChanged(res.data);
      return true;
    }
    alert(errorMessage(res, "Could not save that."));
    return false;
  };

  // "Called them" takes the lead off the call list until our next email goes
  // unanswered; false undoes a mis-click.
  const logCall = (called) => update({ called }, "Could not save that.");

  const notesDirty = notes !== null && notes !== (inquiry && inquiry.adminNotes ? inquiry.adminNotes : "");
  const searchDirty = searchNotes !== null && searchNotes !== (inquiry && inquiry.searchNotes ? inquiry.searchNotes : "");

  // Archive hides a dead lead from the board, saving the notes typed with it,
  // and closes the panel; Unarchive puts it back in its column.
  const setArchived = async (archived) => {
    const body = { archived };
    if (notesDirty) body.adminNotes = notes;
    if (searchDirty) body.searchNotes = searchNotes;
    if (await update(body) && archived) onClose();
  };

  // A guest account opened (here, or by recording a deposit) - the rest of
  // the lead is unchanged, so unsaved notes stay.
  const accountOpened = (account) => {
    setInquiry((i) => ({ ...i, ...accountFields(account) }));
    if (onChanged) onChanged(inquiry);
  };

  const depositChanged = (depositRequest, data = {}) => {
    setInquiry((i) => ({ ...i, depositRequest, ...(data.account ? accountFields(data.account) : {}) }));
    if (onChanged) onChanged(inquiry);
  };

  // A sale needs an account: a customer without one gets a guest account first.
  // `car`: which of the lead's cars (its own when unset).
  const recordSale = async (car) => {
    if (inquiry.userId || inquiry.accountUserId) {
      onRecordPurchase(withCar(inquiry, car));
      return;
    }
    setBusy(true);
    const res = await openDealerGuestAccount({ inquiryId: inquiry._id }).catch(() => null);
    setBusy(false);
    if (res?.data && res.data.account) {
      const next = { ...inquiry, ...accountFields(res.data.account) };
      setInquiry(next);
      onRecordPurchase(withCar(next, car));
    } else {
      alert(errorMessage(res, "Could not open a guest account for the sale.") + " Open it under Account with a corrected phone number.");
    }
  };

  const bookInspection = async (car) => {
    setBusy(true);
    try {
      const res = await createDealerAdminInspection({ inquiryId, ...(car ? { vehicleId: car.vehicleId } : {}) });
      // 409: this car already has one - open that instead.
      const id = res?.data && res.data._id;
      if (id) onOpenInspection(id);
      else alert(errorMessage(res, "Could not open an inspection."));
    } catch (err) {
      alert("Could not open an inspection.");
    } finally {
      setBusy(false);
    }
  };

  // A customer buying more than one car: this one gets a lead of its own and
  // goes through the pipeline by itself. The conversation stays here.
  const splitCar = async (car) => {
    if (!window.confirm(`Move the ${vehicleLabel(car)} to a lead of its own? The conversation stays on this lead.`)) return;
    setBusy(true);
    const res = await splitDealerAdminInquiryCar(inquiryId, car._id).catch(() => null);
    setBusy(false);
    if (res?.message === "Car split off" && res.data) {
      setInquiry((i) => ({ ...i, extraCars: res.data.inquiry.extraCars || [] }));
      setNotice(`The ${vehicleLabel(car)} is a lead of its own now - you'll see it on the board.`);
      if (onChanged) onChanged(res.data.inquiry);
    } else {
      alert(errorMessage(res, "Could not split that car off."));
    }
  };

  // The same customer on the board twice (a general contact form, then a
  // form about a car): this lead's cars, conversation and notes go into the
  // one picked, this one closes, and the panel moves to that lead.
  const startMerge = async () => {
    setBusy(true);
    const res = await getDealerAdminMergeTargets(inquiryId).catch(() => null);
    setBusy(false);
    if (res?.message !== "Success") {
      alert(errorMessage(res, "Could not load their other leads."));
    } else if (!res.data.length) {
      alert("They have no other open lead to move this one into.");
    } else {
      setPicking("");
      setMergeTargets(res.data);
      setMergeInto(res.data[0]._id);
    }
  };

  const runMerge = async () => {
    const target = mergeTargets.find((t) => t._id === mergeInto);
    const into = target && target.cars.length ? `the lead on the ${target.cars[0]}` : "their general lead";
    if (!window.confirm(`Move this lead into ${into}? Its cars, conversation and notes go there, and this lead closes.`)) return;
    if (notesDirty || searchDirty) {
      // Typed and not saved: saved first, so they move with the rest.
      const body = {};
      if (notesDirty) body.adminNotes = notes;
      if (searchDirty) body.searchNotes = searchNotes;
      if (!(await update(body))) return;
    }
    setBusy(true);
    const res = await mergeDealerAdminInquiry(inquiryId, mergeInto).catch(() => null);
    setBusy(false);
    if (res?.message === "Lead merged" && res.data) {
      setMergeTargets(null);
      if (onChanged) onChanged(res.data.inquiry);
      if (onOpenLead) onOpenLead(res.data.inquiry._id);
      else onClose();
    } else {
      alert(errorMessage(res, "Could not move this lead."));
    }
  };

  // One line per lead to move into: its cars, where it stands, when it started.
  const mergeOption = (t) => [
    t.cars.length ? t.cars.join(" + ") : `${INQUIRY_TYPE_LABELS[t.type] || "Lead"} (no car)`,
    INQUIRY_STATUS_LABELS[t.status] || t.status,
    t.archivedAt ? "archived" : "",
    `since ${formatDate(t.createdAt)}`,
  ].filter(Boolean).join(" · ");

  const replyBox = () => threadRef.current && threadRef.current.querySelector("textarea");
  // Photos picked for the reply and not sent yet.
  const replyFiles = () => !!(threadRef.current && threadRef.current.querySelector("[data-pending-files]"));

  // Closing (×, the backdrop, Escape) asks first when something typed would be lost.
  const canClose = () => {
    const draft = replyBox();
    const unsaved = [
      notesDirty && "staff notes",
      searchDirty && "what they want instead",
      ((draft && draft.value.trim()) || replyFiles()) && "your reply",
    ].filter(Boolean);
    return !unsaved.length || window.confirm(`Close without saving ${unsaved.join(" and ")}?`);
  };

  if (!inquiry) {
    return (
      <PanelFrame label="Lead" onClose={onClose} header={<h2>Loading...</h2>} />
    );
  }

  // The lead's own car, then the others they asked about while it was open.
  const cars = leadCars(inquiry);
  const multi = cars.length > 1;
  // Their own account, one they opened since enquiring as a guest, or a guest
  // account staff opened for them.
  const account = inquiry.accountUserId || inquiry.userId
    ? { _id: inquiry.accountUserId || (inquiry.userId && inquiry.userId._id), isGuest: !!inquiry.accountIsGuest }
    : null;
  const email = (inquiry.userId && inquiry.userId.email) || inquiry.email;
  const name = customerName(inquiry);
  const phone = inquiry.phone || (inquiry.userId && inquiry.userId.phone) || "";
  const live = inquiry.status !== "closed";

  const pick = (action) => {
    setMergeTargets(null);
    setPickedCar(cars[0].vehicleId);
    setPicking(action);
  };

  const runPicked = () => {
    const car = cars.find((c) => c.vehicleId === pickedCar);
    setPicking("");
    if (picking === "inspection") bookInspection(car);
    else recordSale(car);
  };

  const header = (
    <>
      <div className={styles.headerTop}>
        <h2>
          {INQUIRY_TYPE_LABELS[inquiry.type] || "Lead"}:{" "}
          {inquiry.vehicle && inquiry.vehicle.url
            ? <a href={inquiry.vehicle.url} target="_blank" rel="noopener noreferrer" className={classes.clickableCell}>{vehicleLabel(inquiry)}</a>
            : vehicleLabel(inquiry)}
          {multi && <span className={styles.hint}> and {cars.length - 1} more {cars.length === 2 ? "car" : "cars"}</span>}
        </h2>
        <div className={styles.inlineRow} style={{ margin: 0 }}>
          {statusSaved && <span className={styles.hint}>Saved</span>}
          <select
            className={`${classes.formInput} ${styles.headerStatus}`}
            aria-label="Lead status"
            value={inquiry.status}
            disabled={busy}
            onChange={(e) => saveStatus(e.target.value)}
          >
            {INQUIRY_STATUSES.map((st) => <option key={st} value={st}>{INQUIRY_STATUS_LABELS[st] || st}</option>)}
          </select>
        </div>
      </div>
      <div className={styles.hint}>
        {/* Their page; it takes this panel's place, so ask about unsaved text first. */}
        <CustomerLink customer={customerRefOf(inquiry, "inquiry")} onOpen={onOpenCustomer && ((ref) => canClose() && onOpenCustomer(ref))}>
          {name}
        </CustomerLink>
        {email && email !== name ? ` · ${email}` : ""}
        {phone && <> · <a href={`tel:${phone}`} className={classes.clickableCell}>{formatPhone(phone)}</a></>}
      </div>
      <div className={styles.headerChips}>
        <span className={`${styles.chip} ${styles.chipMuted}`}>{account ? (account.isGuest ? "Guest account" : "Account") : "No account"}</span>
        {account && (
          <span className={`${styles.chip} ${inquiry.depositBalance > 0 ? styles.chipGood : styles.chipMuted}`}>
            {inquiry.depositBalance > 0 ? `Deposit on file ${formatUsd(inquiry.depositBalance, { cents: false })}` : "No deposit on file"}
          </span>
        )}
        {inquiry.archivedAt && <span className={`${styles.chip} ${styles.chipMuted}`}>Archived {formatDate(inquiry.archivedAt)}</span>}
        <span className={styles.hint}>
          {formatDate(inquiry.createdAt)} · {inquiry.origin === "portal" ? "Portal" : "Website"}
        </span>
      </div>
      <div className={styles.headerActions}>
        {inquiry.vehicleId && !inquiry.mergedInto && (
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={() => (multi ? pick("inspection") : bookInspection())}>
            {!multi && inquiry.type === "inspection" ? "Open the inspection" : "Book an inspection"}
          </button>
        )}
        {inquiry.vehicleId && !inquiry.mergedInto && (inquiry.userId || inquiry.email) && (
          <button type="button" className={styles.btn} disabled={busy} onClick={() => (multi ? pick("sale") : recordSale())}>
            Start the sale
          </button>
        )}
        <MoreMenu
          disabled={busy}
          items={[
            email && { label: "Email in your mail app", href: `mailto:${email}?subject=${encodeURIComponent(`Re: ${vehicleLabel(inquiry)}`)}` },
            inquiry.archivedAt
              ? !inquiry.mergedInto && { label: "Unarchive", onClick: () => setArchived(false) }
              : live && { label: "Archive (dead lead)", title: "Hide it from the pipeline. A reply from the customer brings it back.", onClick: () => setArchived(true) },
            !inquiry.mergedInto && {
              label: "Move into another lead",
              title: "The same customer twice on the board: put this lead's cars, conversation and notes into their other lead, and close this one.",
              onClick: startMerge,
            },
          ]}
        />
      </div>
      {picking && (
        <div className={styles.inlineRow}>
          <label className={styles.hint} htmlFor="lead-car-pick">
            {picking === "inspection" ? "Inspect which car?" : "Which car are they buying?"}
          </label>
          <select
            id="lead-car-pick"
            className={classes.formInput}
            style={{ width: "auto", marginTop: 0 }}
            value={pickedCar}
            onChange={(e) => setPickedCar(e.target.value)}
          >
            {cars.map((car) => <option key={car.vehicleId} value={car.vehicleId}>{vehicleLabel(car)}</option>)}
          </select>
          <button type="button" className={styles.btn} disabled={busy} onClick={runPicked}>
            {picking === "inspection" ? "Book the inspection" : "Start the sale"}
          </button>
          <button type="button" className={styles.btnText} onClick={() => setPicking("")}>Cancel</button>
        </div>
      )}
      {mergeTargets && (
        <div className={styles.inlineRow}>
          <label className={styles.hint} htmlFor="lead-merge-pick">Move into which lead?</label>
          <select
            id="lead-merge-pick"
            className={classes.formInput}
            style={{ width: "auto", marginTop: 0 }}
            value={mergeInto}
            onChange={(e) => setMergeInto(e.target.value)}
          >
            {mergeTargets.map((t) => <option key={t._id} value={t._id}>{mergeOption(t)}</option>)}
          </select>
          <button type="button" className={styles.btn} disabled={busy} onClick={runMerge}>Move it</button>
          <button type="button" className={styles.btnText} onClick={() => setMergeTargets(null)}>Cancel</button>
        </div>
      )}
    </>
  );

  // A lead moved into another has nothing left to do here.
  const att = inquiry.mergedInto ? null : leadAttention(inquiry);
  const attention = (
    <>
      {notice && (
        <div className={`${styles.attention} ${styles.attentionGood}`}>
          <span className={styles.attentionText}>{notice}</span>
          <button type="button" className={styles.btnText} onClick={() => setNotice("")}>Dismiss</button>
        </div>
      )}
      {inquiry.mergedInto && (
        <div className={styles.attention}>
          <span className={styles.attentionText}>Moved into their other lead {formatDate(inquiry.closedAt)} - its cars and conversation are there.</span>
          {onOpenLead && <button type="button" className={styles.btn} onClick={() => onOpenLead(inquiry.mergedInto)}>Open that lead</button>}
        </div>
      )}
      {att && att.kind === "call" && (
        <div className={`${styles.attention} ${styles.attentionWarn}`}>
          <span className={styles.attentionText}>On the call list: our email has had no reply · {ageLabel(att.since)}</span>
          {phone
            ? <a href={`tel:${phone}`}>Call {formatPhone(phone)}</a>
            : <span>No phone number on file.</span>}
          <button type="button" className={styles.btn} disabled={busy} onClick={() => logCall(true)}>Called them</button>
        </div>
      )}
      {att && att.kind === "yourTurn" && (
        <div className={`${styles.attention} ${hoursSince(att.since) > REPLY_ALERT_HOURS ? styles.attentionBad : styles.attentionWarn}`}>
          <span className={styles.attentionText}>Customer replied - your turn · {ageLabel(att.since)}</span>
          <button type="button" className={styles.btn} onClick={() => replyBox() && replyBox().focus()}>Reply</button>
        </div>
      )}
      {att && att.kind === "dealer" && (
        <div className={styles.attention}>
          <span className={styles.attentionText}>
            Waiting on the dealer in Japan since {formatDate(att.since)}
            {inquiry.dealerAsk.note ? ` - ${inquiry.dealerAsk.note}` : ""}
          </span>
        </div>
      )}
      {att && att.kind === "called" && (
        <div className={`${styles.attention} ${styles.attentionGood}`}>
          <span className={styles.attentionText}>Called {formatDate(att.since)} - off the call list until our next email goes a day without a reply.</span>
          <button type="button" className={styles.btnText} disabled={busy} onClick={() => logCall(false)}>Undo</button>
        </div>
      )}
      {att && att.kind === "archived" && (
        <div className={styles.attention}>
          <span className={styles.attentionText}>Archived {formatDate(att.since)} - off the board until they write again.</span>
          <button type="button" className={styles.btn} disabled={busy} onClick={() => setArchived(false)}>Unarchive</button>
        </div>
      )}
    </>
  );

  const touch = firstTouch(inquiry.attribution);
  const sidebar = (
    <>
      {inquiry.status === "searching" && (
        <div className={styles.sideCard}>
          <h3>What they want instead</h3>
          <StaffNotesBox
            id="lead-search-notes"
            label="What they want instead"
            placeholder="Model, budget, mileage, colour..."
            saveLabel="Save"
            rows={2}
            value={searchNotes || ""}
            saved={inquiry.searchNotes}
            onChange={setSearchNotes}
            onSave={saveSearchNotes}
            busy={busy}
          />
        </div>
      )}

      {cars.length > 0 && (
        <div className={styles.sideCard}>
          {/* A customer shopping several cars sends a form on each; they all
              joined this lead, so one conversation covers them. Book the
              inspection or start the sale for the car they choose; split one
              off when they're buying more than one. */}
          <h3>{multi ? `Cars (${cars.length})` : "Car"}</h3>
          <ul className={styles.carList}>
            {cars.map((car) => (
              <CarRow key={car.vehicleId} car={car} busy={busy} onSplit={car.primary ? null : splitCar} />
            ))}
          </ul>
        </div>
      )}

      {live && (
        <div className={styles.sideCard}>
          <h3>Deposit</h3>
          <DepositRequestBox
            target={{ inquiryId: inquiry._id }}
            request={inquiry.depositRequest}
            account={account}
            depositBalance={account ? inquiry.depositBalance : 0}
            suggestedAmount={inquiry.vehicle && inquiry.vehicle.usdPrice ? Math.round(inquiry.vehicle.usdPrice * 0.1) : 0}
            onChanged={depositChanged}
          />
        </div>
      )}

      {live && (
        <div className={styles.sideCard}>
          {/* Leads without a car of ours (one seen on another dealer's site)
              wait on the dealer too. */}
          <h3>Dealer in Japan</h3>
          <DealerWaitBox dealerAsk={inquiry.dealerAsk} onSave={saveDealerWait} />
        </div>
      )}

      <div className={styles.sideCard}>
        <h3>Staff notes</h3>
        <StaffNotesBox
          id="lead-notes"
          rows={4}
          value={notes || ""}
          saved={inquiry.adminNotes}
          onChange={setNotes}
          onSave={saveNotes}
          busy={busy}
        />
      </div>

      {(!account || account.isGuest) && (
        <div className={styles.sideCard}>
          <h3>Account</h3>
          <GuestAccountBox target={{ inquiryId: inquiry._id }} record={inquiry} account={account} onOpened={accountOpened} />
        </div>
      )}

      <div className={styles.sideCard}>
        <h3>Details</h3>
        <dl className={styles.factList}>
          <dt>Received</dt>
          <dd>{new Date(inquiry.createdAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</dd>
          <dt>From</dt>
          <dd>{inquiry.origin === "portal" ? "Portal" : "Website"}</dd>
          {inquiry.page && (
            <>
              <dt>Page</dt>
              <dd>
                {/^https?:\/\//i.test(inquiry.page)
                  ? <a href={inquiry.page} target="_blank" rel="noopener noreferrer" className={classes.clickableCell}>{pagePath(inquiry.page)}</a>
                  : inquiry.page}
              </dd>
            </>
          )}
          {touch && (
            <>
              <dt>First touch</dt>
              <dd>{touch}</dd>
            </>
          )}
        </dl>
      </div>
    </>
  );

  return (
    <PanelFrame
      label={`Lead: ${vehicleLabel(inquiry)}`}
      onClose={onClose}
      canClose={canClose}
      header={header}
      attention={attention}
      sidebar={sidebar}
    >
      {/* The conversation: the customer's original message is the first
          bubble; replies here email the customer a link back to their garage. */}
      <div className={styles.panelSection} ref={threadRef}>
        <h3>Conversation</h3>
        <InquiryThread
          inquiry={inquiry}
          viewer="staff"
          sending={replying}
          // A moved lead's conversation lives in the other lead now.
          onSendReply={inquiry.mergedInto ? () => { alert("This lead was moved into another one - reply there."); return false; } : sendReply}
          replyPlaceholder={inquiry.userId
            ? "Reply - the customer is emailed a link to their garage…"
            : "Reply - this guest receives the full text by email…"}
        />
      </div>
    </PanelFrame>
  );
};

export default InquiryPanel;
